/**
 * naive-ui 的主题覆写(规格 §4.1)。
 *
 * 为什么要覆写而不是用它默认的那套:naive-ui 的默认蓝与我们的品牌蓝差一点点。
 * 「差一点点」是视觉上最亏的一种状态 —— 它不像错,只像没做完。
 * 两端的全部控件都从这里取色,所以「主色」在这个项目里只有一个定义。
 *
 * 两端共用同一个构造器、只在调色板上分叉:圆角、字号、间距、控件高度这些
 * **不该有两套**的东西只有一份。分开写两份的代价是改了一边忘了另一边,
 * 而两端的按钮高度差 2px 这种事没人会报上来。
 */
import type { GlobalThemeOverrides } from 'naive-ui'
import { TOKENS, type Tone } from './tokens'

interface Palette {
  readonly primary: string
  readonly primaryHover: string
  readonly primaryPressed: string
  readonly info: string
  readonly success: string
  readonly warning: string
  readonly danger: string
  /** 正文主色 / 次级 / 三级 */
  readonly text1: string
  readonly text2: string
  readonly text3: string
  readonly border: string
  readonly borderStrong: string
  /** 页面底 / 白面 / 次级面(表头、斑马纹) */
  readonly body: string
  readonly surface: string
  readonly surfaceAlt: string
  /** 浮层底:气泡、下拉菜单 —— 比卡片再亮/再深一档才浮得起来 */
  readonly popover: string
  readonly brandSoft: string
  readonly shadowMd: string
  readonly shadowLg: string
}

function buildOverrides(p: Palette): GlobalThemeOverrides {
  const onBrand = TOKENS.onBrand

  return {
    common: {
      fontFamily: TOKENS.font.sans,
      fontFamilyMono: TOKENS.font.mono,
      fontSize: '14px',
      fontSizeSmall: '13px',
      fontSizeMedium: '14px',
      lineHeight: '1.6',
      borderRadius: TOKENS.radius.md,
      borderRadiusSmall: TOKENS.radius.sm,

      primaryColor: p.primary,
      primaryColorHover: p.primaryHover,
      primaryColorPressed: p.primaryPressed,
      primaryColorSuppl: p.primary,
      infoColor: p.info,
      infoColorHover: p.info,
      infoColorPressed: p.info,
      infoColorSuppl: p.info,
      successColor: p.success,
      successColorHover: p.success,
      successColorPressed: p.success,
      successColorSuppl: p.success,
      warningColor: p.warning,
      warningColorHover: p.warning,
      warningColorPressed: p.warning,
      warningColorSuppl: p.warning,
      errorColor: p.danger,
      errorColorHover: p.danger,
      errorColorPressed: p.danger,
      errorColorSuppl: p.danger,

      textColorBase: p.text1,
      textColor1: p.text1,
      textColor2: p.text2,
      textColor3: p.text3,
      textColorDisabled: p.text3,
      placeholderColor: p.text3,
      placeholderColorDisabled: p.text3,
      iconColor: p.text3,
      iconColorHover: p.primary,
      iconColorPressed: p.primaryPressed,
      iconColorDisabled: p.text3,

      dividerColor: p.border,
      borderColor: p.border,
      bodyColor: p.body,
      cardColor: p.surface,
      modalColor: p.surface,
      popoverColor: p.popover,
      tableColor: p.surface,
      tableHeaderColor: p.surfaceAlt,
      tableColorHover: p.brandSoft,
      tableColorStriped: p.surfaceAlt,
      inputColor: p.surface,
      inputColorDisabled: p.surfaceAlt,
      tagColor: p.surfaceAlt,
      actionColor: p.surfaceAlt,
      hoverColor: p.brandSoft,
      pressedColor: p.brandSoft,

      boxShadow1: p.shadowMd,
      boxShadow2: p.shadowMd,
      boxShadow3: p.shadowLg,
    },

    Button: {
      borderRadiusTiny: TOKENS.radius.sm,
      borderRadiusSmall: TOKENS.radius.sm,
      borderRadiusMedium: TOKENS.radius.md,
      borderRadiusLarge: TOKENS.radius.md,
      heightTiny: '24px',
      heightSmall: '30px',
      heightMedium: '34px',
      heightLarge: '40px',
      fontSizeMedium: '14px',
      fontSizeLarge: '15px',
      colorPrimary: p.primary,
      colorHoverPrimary: p.primaryHover,
      colorPressedPrimary: p.primaryPressed,
      colorFocusPrimary: p.primaryHover,
      colorDisabledPrimary: p.primary,
      textColorPrimary: onBrand,
      textColorHoverPrimary: onBrand,
      textColorPressedPrimary: onBrand,
      textColorFocusPrimary: onBrand,
      textColorPrimaryDisabled: onBrand,
      borderPrimary: `1px solid ${p.primary}`,
      borderHoverPrimary: `1px solid ${p.primaryHover}`,
      borderPressedPrimary: `1px solid ${p.primaryPressed}`,
      borderFocusPrimary: `1px solid ${p.primaryHover}`,
      textColorText: p.text2,
      textColorTextHover: p.primary,
      textColorTextPressed: p.primaryPressed,
      textColorTextFocus: p.primary,
      border: `1px solid ${p.border}`,
      borderHover: `1px solid ${p.borderStrong}`,
      borderFocus: `1px solid ${p.primary}`,
      borderPressed: `1px solid ${p.borderStrong}`,
    },

    Input: {
      borderRadius: TOKENS.radius.md,
      heightMedium: '34px',
      heightLarge: '40px',
      fontSizeMedium: '14px',
      color: p.surface,
      colorFocus: p.surface,
      colorDisabled: p.surfaceAlt,
      textColor: p.text1,
      textColorDisabled: p.text3,
      placeholderColor: p.text3,
      placeholderColorDisabled: p.text3,
      border: `1px solid ${p.border}`,
      borderHover: `1px solid ${p.borderStrong}`,
      borderFocus: `1px solid ${p.primary}`,
      borderDisabled: `1px solid ${p.border}`,
      // 聚焦用一圈品牌色的柔光而不是加粗描边:加粗会让同一行里的控件整体位移 1px
      boxShadowFocus: `0 0 0 2px ${p.brandSoft}`,
      caretColor: p.primary,
      iconColor: p.text3,
      iconColorHover: p.primary,
    },

    Select: {
      peers: {
        InternalSelection: {
          borderRadius: TOKENS.radius.md,
          heightMedium: '34px',
          fontSizeMedium: '14px',
          color: p.surface,
          colorActive: p.surface,
          colorDisabled: p.surfaceAlt,
          textColor: p.text1,
          textColorDisabled: p.text3,
          placeholderColor: p.text3,
          border: `1px solid ${p.border}`,
          borderHover: `1px solid ${p.borderStrong}`,
          borderFocus: `1px solid ${p.primary}`,
          borderActive: `1px solid ${p.primary}`,
          boxShadowFocus: `0 0 0 2px ${p.brandSoft}`,
          boxShadowActive: `0 0 0 2px ${p.brandSoft}`,
          arrowColor: p.text3,
        },
        InternalSelectMenu: {
          borderRadius: TOKENS.radius.md,
          color: p.popover,
          optionTextColor: p.text1,
          optionTextColorActive: p.primary,
          optionTextColorPressed: p.text1,
          optionColorPending: p.brandSoft,
          optionColorActive: p.brandSoft,
          optionHeightMedium: '34px',
          optionFontSizeMedium: '13.5px',
        },
      },
    },

    Card: {
      borderRadius: TOKENS.radius.lg,
      color: p.surface,
      colorEmbedded: p.surfaceAlt,
      borderColor: p.border,
      textColor: p.text1,
      titleTextColor: p.text1,
      titleFontWeight: '600',
      titleFontSizeMedium: '16px',
      paddingMedium: '20px',
      paddingLarge: '24px',
      actionColor: p.surfaceAlt,
      boxShadow: p.shadowMd,
    },

    DataTable: {
      borderColor: p.border,
      borderRadius: TOKENS.radius.lg,
      thColor: p.surfaceAlt,
      thColorHover: p.surfaceAlt,
      thTextColor: p.text3,
      thFontWeight: '500',
      thPaddingMedium: '9px 14px',
      tdColor: p.surface,
      tdColorHover: p.brandSoft,
      tdColorStriped: p.surfaceAlt,
      tdTextColor: p.text1,
      // 表格里的数字要纵向对齐,行高统一 44px(见 DataTable.vue 的 :deep 规则)
      tdPaddingMedium: '12px 14px',
      fontSizeMedium: '13px',
      lineHeight: '1.5',
      paginationMargin: '14px 0 0',
    },

    Tag: {
      borderRadius: TOKENS.radius.pill,
      heightSmall: '22px',
      heightMedium: '26px',
      fontSizeSmall: '12px',
      fontSizeMedium: '12.5px',
      color: p.surfaceAlt,
      textColor: p.text2,
      border: `1px solid ${p.border}`,
    },

    Drawer: {
      color: p.surface,
      textColor: p.text1,
      titleTextColor: p.text1,
      titleFontSize: '18px',
      titleFontWeight: '600',
      boxShadow: p.shadowLg,
      lineHeight: '1.6',
      headerPadding: '16px 24px',
      bodyPadding: '20px 24px',
      footerPadding: '12px 24px',
      headerBorderBottom: `1px solid ${p.border}`,
      footerBorderTop: `1px solid ${p.border}`,
      closeIconColor: p.text3,
      closeIconColorHover: p.text1,
    },

    Modal: {
      color: p.surface,
      textColor: p.text1,
      titleTextColor: p.text1,
      titleFontSize: '18px',
      titleFontWeight: '600',
      borderRadius: TOKENS.radius.lg,
      padding: '20px 24px',
      closeIconColor: p.text3,
      closeIconColorHover: p.text1,
      iconColor: p.text3,
      boxShadow: p.shadowLg,
    },

    Pagination: {
      itemBorderRadius: TOKENS.radius.md,
      itemFontSizeMedium: '13px',
      itemColor: p.surface,
      itemColorHover: p.brandSoft,
      itemColorPressed: p.brandSoft,
      itemColorActive: p.primary,
      itemTextColor: p.text2,
      itemTextColorHover: p.primary,
      itemTextColorPressed: p.primaryPressed,
      itemTextColorActive: onBrand,
      itemTextColorDisabled: p.text3,
      itemBorder: `1px solid ${p.border}`,
      itemBorderHover: `1px solid ${p.borderStrong}`,
      itemBorderActive: `1px solid ${p.primary}`,
      itemBorderDisabled: `1px solid ${p.border}`,
    },

    Popover: {
      color: p.popover,
      textColor: p.text1,
      borderRadius: TOKENS.radius.lg,
      boxShadow: p.shadowLg,
      fontSize: '13px',
      padding: '12px 14px',
      dividerColor: p.border,
    },

    Timeline: {
      lineColor: p.border,
      circleBorder: `2px solid ${p.primary}`,
      titleTextColor: p.text1,
      contentTextColor: p.text2,
      metaTextColor: p.text3,
      titleFontSizeMedium: '13.5px',
      titleFontWeight: '600',
      contentFontSize: '13px',
    },

    Message: {
      color: p.surface,
      textColor: p.text1,
      borderRadius: TOKENS.radius.md,
      boxShadow: p.shadowMd,
      iconColorInfo: p.info,
      iconColorSuccess: p.success,
      iconColorWarning: p.warning,
      iconColorError: p.danger,
      lineHeight: '1.6',
    },
  }
}

/**
 * 运营端(浅色)。主色 brand.600 —— 白底上 500 会发飘,600 才压得住。
 */
export const opsThemeOverrides: GlobalThemeOverrides = buildOverrides({
  primary: TOKENS.brand[600],
  primaryHover: TOKENS.brand[500],
  primaryPressed: TOKENS.brand[700],
  info: TOKENS.semantic.light.info,
  success: TOKENS.semantic.light.success,
  warning: TOKENS.semantic.light.warning,
  danger: TOKENS.semantic.light.danger,
  text1: TOKENS.light.text1,
  text2: TOKENS.light.text2,
  text3: TOKENS.light.text3,
  border: TOKENS.light.border,
  borderStrong: TOKENS.light.borderStrong,
  body: TOKENS.light.appBg,
  surface: TOKENS.light.surface,
  surfaceAlt: TOKENS.light.surfaceAlt,
  popover: TOKENS.light.surface,
  brandSoft: TOKENS.semantic.alpha.light.brandBg,
  shadowMd: TOKENS.shadow.light.md,
  shadowLg: TOKENS.shadow.light.lg,
})

/**
 * 深色档(监管端;运营端深色另见下面的 opsDarkThemeOverrides)。
 * 主色 brand.500 —— 深底上 600 太闷,和背景糊在一起。
 * 浮层用 panelAlt 而不是 panel:同一块底上叠一个同色的层,分界只剩一条描边。
 */
const govBase: GlobalThemeOverrides = buildOverrides({
  primary: TOKENS.brand[500],
  primaryHover: TOKENS.brand[400],
  primaryPressed: TOKENS.brand[900],
  info: TOKENS.semantic.dark.info,
  success: TOKENS.semantic.dark.success,
  warning: TOKENS.semantic.dark.warning,
  danger: TOKENS.semantic.dark.danger,
  text1: TOKENS.dark.text1,
  text2: TOKENS.dark.text2,
  text3: TOKENS.dark.text3,
  border: TOKENS.dark.border,
  borderStrong: TOKENS.dark.borderStrong,
  body: TOKENS.dark.bgBase,
  surface: TOKENS.dark.panel,
  surfaceAlt: TOKENS.dark.panelAlt,
  popover: TOKENS.dark.panelAlt,
  brandSoft: TOKENS.semantic.alpha.dark.brandBg,
  shadowMd: TOKENS.shadow.dark.sm,
  shadowLg: TOKENS.shadow.dark.glow,
})

/**
 * 监管端 = 深色档 + **输入框的底**那一个覆盖(S-48 收口)。
 *
 * 画板 G4 把登录卡的输入框底画成比卡面更深一档的 `$gov-bg-deep`
 * (= `dark.bgDeep`):登录卡是面板色,输入框再压深一档才看得出「这里可以打字」。
 * 它原来落在 `panel`(与卡同色),两者同为 panel 时输入框只剩一圈描边 ——
 * 页面上完全正常,只是那一格不像一个能输入的框。
 *
 * 为什么写成「深色档 + 一个 Input 覆盖」而不是再调一遍 buildOverrides:
 * 与下面 ops-dark 同一条理由 —— 圆角、控件高度、字号这些**不该有两套**的东西
 * 留在同一份构造里,分叉的只有画板明确画了的那一格。
 * 代价(已知、接受):监管端所有输入控件(综合监控页将来的筛选、日期控件)一起变深;
 * 今天监管端只有登录页那两个输入框,它们要的正是这一档。
 */
export const govThemeOverrides: GlobalThemeOverrides = {
  ...govBase,
  Input: {
    ...(govBase.Input ?? {}),
    color: TOKENS.dark.bgDeep,
    colorFocus: TOKENS.dark.bgDeep,
  },
}

/**
 * 运营端深色(S-41)。与深色档同一套,只差**输入框的底**:画板 O8 画的是
 * 页面底色(`$ops-dark-bg` #0A1120 = `dark.bgBase`),而登录卡是面板色 ——
 * 与监管端那一档(bgDeep)差一个色阶,两个画板各自量出来的值,谁也不许顺手统一。
 *
 * 为什么写成「深色档 + 一个 Input 覆盖」而不是再调一遍 buildOverrides:
 * 两端的圆角、控件高度、字号这些**不该有两套**的东西留在同一份构造里,
 * 将来改一处两端一起走;分叉的只有这一个画板明确画了的值。
 *
 * naive-ui 的主题变量默认是**内联**挂在元素上的(`inline-theme-disabled` 默认 false),
 * 视图里用 `:deep(.n-input){ --n-color: … }` 覆盖不动它 —— 所以这一处只能改在主题层,
 * 改了才真的生效。
 */
export const opsDarkThemeOverrides: GlobalThemeOverrides = {
  ...govThemeOverrides,
  Input: {
    ...(govThemeOverrides.Input ?? {}),
    color: TOKENS.dark.bgBase,
    colorFocus: TOKENS.dark.bgBase,
  },
}

/**
 * tone → naive-ui 的主题覆写。第三档('ops-dark')与深色档只差输入框那一处,
 * 两处调用点(AppShell / LoginLayout)都走这里 —— 各自的 `tone === 'dark' ? … : …`
 * 三元在第三档出现之后都会把它判成浅色。
 */
export function themeOverridesFor(tone: Tone): GlobalThemeOverrides {
  if (tone === 'light') return opsThemeOverrides
  if (tone === 'ops-dark') return opsDarkThemeOverrides
  return govThemeOverrides
}
